/**
 * Returns true when `b` is later than `a`. Null `b` never wins; null `a` always
 * loses to a non-null `b`. Used to pick the leaf with the latest
 * `complianceMonitoringAssessment.CreatedAtTimestamp` across an entity's
 * second-line results.
 */
export const isLater = (a: string | null, b: string | null): boolean => {
  if (b === null) {
    return false;
  }
  if (a === null) {
    return true;
  }

  return new Date(b).getTime() > new Date(a).getTime();
};

/**
 * Normalises an oversight result row's timestamp column to `null` when absent.
 *
 * The tRPC bridge hooks reshape service-resolved rows back into the GraphQL
 * response shape, and have to emit `''` for timestamps the service does not
 * carry: the generated row types declare `CreatedAtTimestamp` (and the control
 * row's `TestDate`) as non-nullable `string`, so `null` is not assignable
 * without a cast.
 *
 * `''` parses to `NaN`, and because `NaN !== NaN` an empty `TestDate` would send
 * `isMoreRecentByTestDate` down its TestDate branch — silently skipping the
 * `CreatedAt` tie-break rather than treating the timestamp as missing. Coercing
 * `''` to `null` here keeps the "absent timestamp" semantics the comparator is
 * written against, so the reduction stays correct regardless of how many rows a
 * branch emits per parent.
 */
export const toTimestampOrNull = (
  value: string | null | undefined
): string | null =>
  value === null || value === undefined || value === '' ? null : value;

/**
 * A timestamp pair carried by an oversight result row, used to decide which of
 * two results is the more recent. `TestDate` and `CreatedAt` (the result row's
 * `CreatedAtTimestamp`) are read directly from the result row itself.
 */
export type OversightResultTimestamps = {
  TestDate: string | null;
  CreatedAt: string | null;
};

const toTime = (value: string | null): number | null =>
  value === null ? null : new Date(value).getTime();

/**
 * Returns true when candidate `b` is a more recent oversight result than `a`,
 * using the SAME ordering the entity's own page uses to pick its "latest"
 * oversight result (`getLatestOversightAssessment*By*Id`): `TestDate`
 * descending (nulls last), tie-broken by `CreatedAtTimestamp` descending — both
 * read from the result row itself. Keeping this identical to the entity-page
 * queries guarantees a register and the entity page never disagree on which
 * result is current, so a newly assigned or re-dated result surfaces in both.
 */
export const isMoreRecentByTestDate = (
  a: OversightResultTimestamps,
  b: OversightResultTimestamps
): boolean => {
  const aTest = toTime(a.TestDate);
  const bTest = toTime(b.TestDate);
  if (aTest !== bTest) {
    // desc_nulls_last: a non-null TestDate always beats a null one.
    if (bTest === null) {
      return false;
    }
    if (aTest === null) {
      return true;
    }

    return bTest > aTest;
  }

  // Equal (or both-null) TestDate — tie-break on CreatedAtTimestamp desc.
  return (toTime(b.CreatedAt) ?? 0) > (toTime(a.CreatedAt) ?? 0);
};
